import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { connect, useDispatch } from 'react-redux';

import i18n from 'I18n';

import GenderPicker from 'component/account/GenderPicker';
import CustomButton from 'component/base/CustomButton';

import {
  CUSTOMER_SIGN_UP_METHOD,
  SupportedCountryCodeList,
} from 'utils/CustomEnums';

import {
  SHOW_ONLINE_EMAIL_REMINDER,
  SECOND_CONTACT_HIDE_OTP,
  PRIMARY_SIGN_UP_METHOD,
  LOGIN_OPTIONAL,
} from 'utils/Config';
import {
  SECOND_CONTACT_OPTIONAL,
  EDIT_MOBILE_OPTIONAL,
  EDIT_EMAIL_OPTIONAL,
  MOBILE_AND_EMAIL_BOTH_FOR_SIGN_UP,
} from 'utils/LoginOrSecondContactUtils';
import './LoginMethodContactModal.scss';
import { createAction } from 'utils';

const sections = SupportedCountryCodeList.map((item) => ({
  name: item,
  pk: item,
}));

const MaxTimeCount = 60;

const DirectMarketingModal = ({
  agreeDirectMarketingOnPress,
  disagreeDirectMarketingOnPress,
}) => {
  return (
    <div>
      <div className="home-profile-custom-title profile-title-color title-medium-1">
        {i18n.t('account_direct_marketing')}
      </div>
      <div className="direct-marketing-section-content body-1">
        {i18n.t('personal_info_receive_marketing_reminder')}
      </div>
      <div>
        <CustomButton
          className="direct-marketing-section-button direct-marketing-section-agree-button"
          disabled={false}
          text={i18n.t('settings_agree_direct_marketing')}
          btnClicked={agreeDirectMarketingOnPress}
        />
      </div>
      <div>
        <CustomButton
          className="direct-marketing-section-button direct-marketing-section-disagree-button"
          disabled={false}
          text={i18n.t('settings_disagree_direct_marketing')}
          btnClicked={disagreeDirectMarketingOnPress}
        />
      </div>
    </div>
  )
}

const EditEmailReminderModal = ({
  emailReminderDoneOnPress,
}) => (
  <div>
    <div className='home-profile-custom-title profile-title-color headline'>
      {i18n.t("edit_email_reminder_title")}
    </div>
    <div className='edit-email-reminder-content body-1'>
      {i18n.t("edit_email_reminder_content")}
    </div>
    <div>
      <CustomButton
        className="direct-marketing-section-button"
        disabled={false}
        text={i18n.t('edit_email_reminder_done')}
        btnClicked={emailReminderDoneOnPress}
      />
    </div>
  </div>
);

const EditMobileSection = ({
  countryCode,
  selectCountryCode,
  nationalNumber,
  setNationalNumber,
  hideOtp,
  verifyEnable,
  verifyText,
  callApiRequireOTP,
  contactError,
}) => (
  <div
    className={
      contactError
        ? 'home-profile-mobile has-error'
        : 'home-profile-mobile'
    }
  >
    <div className="home-profile-custom-mobile country-code bottom-line common-item-value field-text">
      <GenderPicker
        customClass={'country-code-picker-box'}
        sections={sections}
        value={countryCode}
        selectValue={selectCountryCode}
      />
    </div>
    <div className="home-profile-custom-mobile national-number bottom-line common-item-value field-text">
      <input
        placeholder={i18n.t('account_mobile')}
        className="mobile-input field-text"
        value={nationalNumber || ""}
        onChange={(e) => {
          const text = e.target.value;
          setNationalNumber(text);
        }}
      />
      {
        hideOtp
          ? null
          : <CustomButton
            className="button-small"
            disabled={!verifyEnable}
            text={verifyText}
            btnClicked={callApiRequireOTP}
          />
      }
    </div>
  </div>
);

const EditEmailSection = ({
  email,
  setEmail,
  hideOtp,
  verifyEnable,
  verifyText,
  callApiRequireOTP,
  contactError,
}) => (
  <div className={contactError ? 'has-error' : ''}>
    <div className="home-profile-custom-email bottom-line common-item-value field-text">
      <input
        placeholder={i18n.t('settings_email')}
        className="email-input field-text"
        value={email || ""}
        onChange={(e) => {
          const text = e.target.value;
          setEmail(text);
        }}
      />
      {!hideOtp && (
        <CustomButton
          className="button-small"
          disabled={!verifyEnable}
          text={verifyText}
          btnClicked={() => callApiRequireOTP()}
        />
      )}
    </div>
    {MOBILE_AND_EMAIL_BOTH_FOR_SIGN_UP && (
      <div className="account-common-prompt-information tag-menu-1">
        {i18n.t('settings_email_tip')}
      </div>
    )}
  </div>
);

const EditSecondLoginOrContactMethodModal = ({
  modalMethod,
  onClose,
  countryCode,
  selectCountryCode,
  nationalNumber,
  setNationalNumber,
  email,
  setEmail,
  hideOtp,
  verifyEnable,
  verifyText,
  callApiRequireOTP,
  contactError,
  contactErrorType,
  contactErrorMessage,
  verifyCode,
  setVerifyCode,
  verifyError,
  confirmEnable,
  confirmOnPress,
  setShowCustomerContactModal,
}) => {
  const modalTitle = useMemo(() => {
    // check is used for login
    if (
      modalMethod === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER
      && [
        CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER,
        CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER_AND_EMAIL_ADDRESS
      ].includes(PRIMARY_SIGN_UP_METHOD)
    ) {
      return LOGIN_OPTIONAL
        ? i18n.t('settings_login_and_contact') + i18n.t('personal_info_optional')
        : i18n.t('settings_login_and_contact');
    };
    if (
      modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS
      && [
        CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS,
        CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER_AND_EMAIL_ADDRESS
      ].includes(PRIMARY_SIGN_UP_METHOD)
    ) {
      return LOGIN_OPTIONAL
        ? i18n.t('settings_login_and_contact') + i18n.t('personal_info_optional')
        : i18n.t('settings_login_and_contact');
    };

    // check second contact is optional
    if (SECOND_CONTACT_OPTIONAL) {
      return i18n.t('settings_contact_for_direct_marketing') + i18n.t('personal_info_optional');
    };

    return i18n.t('settings_contact_for_direct_marketing');
  }, [modalMethod]);

  return (
    <div className="second-contact-modal-container">
      <div className="home-profile-custom-title title-medium-1">
        <div>
          {modalTitle}
        </div>
        <div className="close-icon" onClick={onClose}>
          <img
            alt="close"
            src="/profile/close.png"
            srcSet="/profile/close@2x.png 2x, /profile/close@3x.png 3x"
          />
        </div>
      </div>
      <div className="contact-input-section">
        {modalMethod === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER && (
          <EditMobileSection
            countryCode={countryCode}
            selectCountryCode={selectCountryCode}
            nationalNumber={nationalNumber}
            setNationalNumber={setNationalNumber}
            hideOtp={hideOtp}
            verifyEnable={verifyEnable}
            verifyText={verifyText}
            callApiRequireOTP={callApiRequireOTP}
            contactError={contactError}
          />
        )}

        {modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS && (
          <EditEmailSection
            email={email}
            setEmail={setEmail}
            hideOtp={hideOtp}
            verifyEnable={verifyEnable}
            verifyText={verifyText}
            callApiRequireOTP={callApiRequireOTP}
            contactError={contactError}
          />
        )}

        {contactError && contactErrorMessage
          ? <div className="account-common-prompt-information change-password-error tag-menu-2">
            {contactErrorMessage}
          </div>
          : null}
        {contactError && contactErrorType === "duplicateContactError" ?
          <div className='second-contact-line'>
            <button
              className="add-another-btn button-small subheading"
              onClick={() => {
                onClose()
                setShowCustomerContactModal({
                  show: true,
                  isForSecondContact: true,
                  secondContact: modalMethod === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER
                    ? `+${countryCode || ""} ${nationalNumber}`
                    : modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS
                      ? email : "",
                })
              }
              }
            >
              {i18n.t('contact_customer_service')}
            </button>
          </div>
          : null
        }

      </div>

      {!hideOtp && (
        <div>
          <div
            className={`home-profile-custom-verify-code bottom-line common-item-value field-text ${verifyError ? 'has-error' : ''
              }`}
          >
            <input
              placeholder={i18n.t('sign_up_verification_code')}
              className="field-text"
              onChange={(e) => {
                const text = e.target.value;
                setVerifyCode(text);
              }}
            />
          </div>
          <div className="account-common-prompt-information change-password-error tag-menu-2">
            {verifyError || ''}
          </div>
        </div>
      )}
      <CustomButton
        className="button-large"
        disabled={!confirmEnable}
        text={i18n.t('account_update')}
        btnClicked={confirmOnPress}
      />
    </div>
  )
};

const ModalSection = ({
  showDirectMarketingModal,
  agreeDirectMarketingOnPress,
  disagreeDirectMarketingOnPress,
  showEditEmailReminderModal,
  emailReminderDoneOnPress,
  onClose,
  modalMethod,
  countryCode,
  selectCountryCode,
  nationalNumber,
  setNationalNumber,
  email,
  setEmail,
  hideOtp,
  verifyEnable,
  verifyText,
  callApiRequireOTP,
  contactError,
  contactErrorType,
  contactErrorMessage,
  verifyCode,
  setVerifyCode,
  verifyError,
  confirmEnable,
  confirmOnPress,
  setShowCustomerContactModal,
}) => {
  if (showDirectMarketingModal) {
    return <DirectMarketingModal
      agreeDirectMarketingOnPress={agreeDirectMarketingOnPress}
      disagreeDirectMarketingOnPress={disagreeDirectMarketingOnPress}
    />
  };

  if (showEditEmailReminderModal) {
    return <EditEmailReminderModal emailReminderDoneOnPress={emailReminderDoneOnPress} />
  };

  return <EditSecondLoginOrContactMethodModal
    modalMethod={modalMethod}
    onClose={onClose}
    countryCode={countryCode}
    selectCountryCode={selectCountryCode}
    nationalNumber={nationalNumber}
    setNationalNumber={setNationalNumber}
    email={email}
    setEmail={setEmail}
    hideOtp={hideOtp}
    verifyEnable={verifyEnable}
    verifyText={verifyText}
    callApiRequireOTP={callApiRequireOTP}
    contactError={contactError}
    contactErrorType={contactErrorType}
    contactErrorMessage={contactErrorMessage}
    verifyCode={verifyCode}
    setVerifyCode={setVerifyCode}
    verifyError={verifyError}
    confirmEnable={confirmEnable}
    confirmOnPress={confirmOnPress}
    setShowCustomerContactModal={setShowCustomerContactModal}
  />
};

const LoginMethodContactModal = ({
  language,
  tempCountryCode,
  tempPhoneNumber,
  tempEmailAddress,
  userCountryCode,
  userPhoneNumber,
  userEmail,
  hasAgreedDirectMarketing,
  contactError,
  contactErrorType,
  contactErrorMessage,
  verifyChallenge,
  verifyError,
  showModal,
  modalMethod,
  onClose = () => { },
  setShowCustomerContactModal = () => { },
}) => {
  const dispatch = useDispatch();

  const afterActionShowEmailReminder = (
    SHOW_ONLINE_EMAIL_REMINDER
    && modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS
  )

  const defaultCountryCode = userCountryCode || tempCountryCode || SupportedCountryCodeList?.[0];
  const defaultPhoneNumber = userPhoneNumber || tempPhoneNumber || null;
  const defaultEmail = userEmail || tempEmailAddress || null;

  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const selectCountryCode = useCallback((value) => setCountryCode(value), []);
  const [nationalNumber, setNationalNumber] = useState(defaultPhoneNumber);
  const [email, setEmail] = useState(defaultEmail);
  const [requiringOPT, setRequiringOPT] = useState(false);
  const [verifyCode, setVerifyCode] = useState(null);

  const [showDirectMarketingModal, setShowDirectMarketingModal] = useState(false);
  const [showEditEmailReminderModal, setShowEditEmailReminderModal] = useState(false);

  // otp logic start
  const hideOtp = useMemo(() => {
    if (!SECOND_CONTACT_HIDE_OTP) {
      return false;
    };

    if (
      modalMethod === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER
      && PRIMARY_SIGN_UP_METHOD === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS
    ) {
      return true;
    };
    if (
      modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS
      && PRIMARY_SIGN_UP_METHOD === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER
    ) {
      return true;
    };

    return false;
  }, [modalMethod]);

  // after send otp, disable send otp again in several seconds
  const [refreshDatetime, setRefreshDatetime] = useState(Date.now());
  useEffect(() => {
    setRefreshDatetime(Date.now());
    const intervalID = setInterval(() => {
      setRefreshDatetime(Date.now());
    }, 100);
    return () => {
      // cancel refresh qrcode
      if (intervalID) {
        clearInterval(intervalID);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [callOTPTime, setCallOTPTime] = useState(null);

  const verifyEnable = useMemo(() => {
    // is calling otp
    if (requiringOPT) {
      return false;
    }
    // disable in a period of time after called otp
    if (callOTPTime !== null) {
      const ms = Number(refreshDatetime) - Number(callOTPTime);
      const seconds = Math.round(ms / 1000);
      if (seconds < MaxTimeCount) {
        return false;
      }
    }

    // login or contact method is empty or not change
    if (modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS) {
      if (!email || email === userEmail) {
        return false;
      }
    } else if (modalMethod === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER) {
      if (
        !countryCode
        || !nationalNumber
        || (
          countryCode === userCountryCode
          && nationalNumber === userPhoneNumber
        )) {
        return false;
      }
    };

    return true;
  }, [requiringOPT, refreshDatetime, callOTPTime]);

  const verifyText = useMemo(() => {
    if (callOTPTime !== null) {
      const ms = Number(refreshDatetime) - Number(callOTPTime);
      const seconds = Math.round(ms / 1000);
      if (seconds < MaxTimeCount) {
        return i18n.t('account_verify') + ` (${MaxTimeCount - seconds}s)`;
      }
    }
    return i18n.t('account_verify');
  }, [refreshDatetime, callOTPTime]);

  const callApiRequireOTP = () => {
    setRequiringOPT(true);
    dispatch(createAction('userInfoTemp/requireOTPForUpdateLoginMethod')({
      method: modalMethod,
      countryCode,
      nationalNumber,
      email,
      successCallback: () => {
        setRequiringOPT(false);
        setCallOTPTime(Date.now());
      },
      failureCallback: () => setRequiringOPT(false),
    }));
  };
  // otp logic end

  // update login or contact method logic start
  const confirmEnable = useMemo(
    () => {
      // verify challenge and verify code is provided
      if (!!verifyChallenge && !!verifyCode) {
        return true;
      };
      // no verify challenge and verify code
      if (hideOtp) {
        // edit mobile and not empty
        if (modalMethod === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER && countryCode && nationalNumber) {
          return true;
        };
        // edit email and not empty
        if (modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS && email) {
          return true;
        }
      };

      // mobile is empty
      if (modalMethod === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER && !nationalNumber && EDIT_MOBILE_OPTIONAL) {
        return true
      }

      // email is empty
      if (modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS && !email && EDIT_EMAIL_OPTIONAL) {
        return true
      }

      return false;
    },
    [modalMethod, verifyChallenge, verifyCode, email, countryCode, nationalNumber]
  );

  const callApiUpdateLoginMethod = () => {
    dispatch(createAction('userInfoTemp/verifyOTPAndUpdateLoginMethod')({
      method: modalMethod,
      countryCode,
      nationalNumber,
      email,
      verifyCode,
      afterActionShowEmailReminder,
      successCallback: () => {
        // have online store and edit email
        if (afterActionShowEmailReminder) {
          setShowEditEmailReminderModal(true);
          return;
        };
        onClose();
      },
      failureCallback: () => { },
    }));
  };

  const confirmOnPress = () => {
    // customer have agree direct marketing
    if (hasAgreedDirectMarketing) {
      callApiUpdateLoginMethod();
      return;
    }

    // customer not agree direct marketing and edit second contact
    const editMobile = (
      modalMethod === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER
      && PRIMARY_SIGN_UP_METHOD === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS
      && nationalNumber
    );
    const editEmail = (
      modalMethod === CUSTOMER_SIGN_UP_METHOD.EMAIL_ADDRESS
      && PRIMARY_SIGN_UP_METHOD === CUSTOMER_SIGN_UP_METHOD.MOBILE_PHONE_NUMBER
      && email
    );

    // show direct marketing modal
    if (editMobile || editEmail) {
      setShowDirectMarketingModal(true);
      return;
    };

    // default
    callApiUpdateLoginMethod();
  };

  const agreeDirectMarketingOnPress = () => {
    dispatch(createAction('userInfoTemp/updateUserDirectMarketing')({
      hasAgreedDirectMarketing: true,
      directMarketingViaEmail: true,
      afterAction: () => {
        callApiUpdateLoginMethod();
        setShowDirectMarketingModal(false);
      }
    }));
  };

  const disagreeDirectMarketingOnPress = () => {
    callApiUpdateLoginMethod();
    setShowDirectMarketingModal(false);
  };

  const emailReminderDoneOnPress = () => {
    dispatch(createAction('users/fetchUserInfo')({}));
    onClose();
  };

  const onHide = () => {
    // after show email reminder, fetch user info
    if (showEditEmailReminderModal && afterActionShowEmailReminder) {
      dispatch(createAction('users/fetchUserInfo')({}));
    };
    onClose();
  };
  // update login or contact method logic end

  // init data
  useEffect(() => {
    setShowDirectMarketingModal(false);
    setShowEditEmailReminderModal(false);
    setVerifyCode(null);
    setCallOTPTime(null);
    if (showModal) {
      setEmail(defaultEmail);
      setCountryCode(defaultCountryCode);
      setNationalNumber(defaultPhoneNumber);
    }
    dispatch({ type: 'userInfoTemp/clearState', payload: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  // clear challenge when input change
  useEffect(() => {
    dispatch({ type: 'userInfoTemp/clearState', payload: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, countryCode, nationalNumber]);

  return (
    <Modal
      size="lg"
      show={showModal}
      onHide={onHide}
      scrollable={false}
      className={'login-method-modal'}
      centered
    >
      <Modal.Body className={'login-method-modal-body '}>
        <ModalSection
          showDirectMarketingModal={showDirectMarketingModal}
          agreeDirectMarketingOnPress={agreeDirectMarketingOnPress}
          disagreeDirectMarketingOnPress={disagreeDirectMarketingOnPress}
          showEditEmailReminderModal={showEditEmailReminderModal}
          emailReminderDoneOnPress={emailReminderDoneOnPress}
          onClose={onClose}
          modalMethod={modalMethod}
          countryCode={countryCode}
          selectCountryCode={selectCountryCode}
          nationalNumber={nationalNumber}
          setNationalNumber={setNationalNumber}
          email={email}
          setEmail={setEmail}
          hideOtp={hideOtp}
          verifyEnable={verifyEnable}
          verifyText={verifyText}
          callApiRequireOTP={callApiRequireOTP}
          contactError={contactError}
          contactErrorType={contactErrorType}
          contactErrorMessage={contactErrorMessage}
          verifyCode={verifyCode}
          setVerifyCode={setVerifyCode}
          verifyError={verifyError}
          confirmEnable={confirmEnable}
          confirmOnPress={confirmOnPress}
          setShowCustomerContactModal={setShowCustomerContactModal}
        />
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = ({ users, userInfoTemp }) => {
  return {
    language: users.language,
    tempCountryCode: users.userInfo.tempCountryCode,
    tempPhoneNumber: users.userInfo.tempPhoneNumber,
    tempEmailAddress: users.userInfo.tempEmailAddress,
    userCountryCode: users.userInfo.countryCode,
    userPhoneNumber: users.userInfo.phoneNumber,
    userEmail: users.userInfo.emailAddress,
    hasAgreedDirectMarketing: users.userInfo.hasAgreedDirectMarketing,
    contactError: userInfoTemp.loginMethodContact.error,
    contactErrorType: userInfoTemp.loginMethodContact.type,
    contactErrorMessage: userInfoTemp.loginMethodContact.errorMessage,
    verifyChallenge: userInfoTemp.verifyChallenge,
    verifyError: userInfoTemp.verifyError,
  };
};

export default connect(mapStateToProps)(LoginMethodContactModal);
